import React, { useEffect, useMemo, useState } from "react";

import api from "@/api/axios";
import { API } from "@/App";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Video } from "lucide-react";
import { toast } from "react-hot-toast";

const TIME_ZONE = "Asia/Kolkata";

const pad = (n) => String(n).padStart(2, "0");

const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const toDateTimeInput = (d) => `${toDateInput(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Google treats an all-day end date as exclusive, so the last covered day + 1. */
const nextDay = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const next = new Date(y, m - 1, d + 1);
  return toDateInput(next);
};

const defaultForm = () => {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  const end = new Date(start.getTime() + 60 * 60 * 1000);

  return {
    summary: "",
    description: "",
    location: "",
    allDay: false,
    startDateTime: toDateTimeInput(start),
    endDateTime: toDateTimeInput(end),
    startDate: toDateInput(start),
    endDate: toDateInput(start),
    attendees: "",
    withMeet: false,
  };
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Creates an event on the signed-in user's Google Calendar without leaving the
 * app. Optionally attaches a Google Meet link and invites attendees by email —
 * Google sends those invitations itself.
 */
export default function GoogleEventDialog({ open, onOpenChange, onCreated, defaultDate }) {
  const [form, setForm] = useState(defaultForm);
  const [busy, setBusy] = useState(false);

  // Reopening should start clean, and on a day the user clicked if there was one.
  useEffect(() => {
    if (!open) return;
    const fresh = defaultForm();
    if (defaultDate) {
      fresh.startDate = defaultDate;
      fresh.endDate = defaultDate;
      fresh.startDateTime = `${defaultDate}T${fresh.startDateTime.split("T")[1]}`;
      fresh.endDateTime = `${defaultDate}T${fresh.endDateTime.split("T")[1]}`;
    }
    setForm(fresh);
  }, [open, defaultDate]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const attendeeList = useMemo(
    () =>
      form.attendees
        .split(/[,\s;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [form.attendees]
  );

  const badEmails = attendeeList.filter((e) => !EMAIL_RE.test(e));

  const validate = () => {
    if (!form.summary.trim()) return "Give the event a title.";
    if (badEmails.length) return `Not a valid email: ${badEmails[0]}`;

    if (form.allDay) {
      if (!form.startDate) return "Pick a start date.";
      if (form.endDate && form.endDate < form.startDate)
        return "The end date is before the start date.";
    } else {
      if (!form.startDateTime || !form.endDateTime) return "Pick a start and end time.";
      if (form.endDateTime <= form.startDateTime)
        return "The end time must be after the start time.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const problem = validate();
    if (problem) {
      toast.error(problem);
      return;
    }

    const payload = {
      summary: form.summary.trim(),
      description: form.description.trim() || undefined,
      location: form.location.trim() || undefined,
      timeZone: TIME_ZONE,
      withMeet: form.withMeet,
      ...(attendeeList.length ? { attendees: attendeeList } : {}),
    };

    if (form.allDay) {
      payload.startDate = form.startDate;
      // The picker is inclusive ("last day of the event"); Google's end.date is
      // exclusive, so the day after the one chosen is what actually gets sent.
      payload.endDate = nextDay(form.endDate || form.startDate);
    } else {
      // Sent as naive local time alongside timeZone, so Google resolves it in
      // Asia/Kolkata rather than in whatever zone the browser happens to be in.
      payload.startDateTime = `${form.startDateTime}:00`;
      payload.endDateTime = `${form.endDateTime}:00`;
    }

    setBusy(true);
    try {
      const { data } = await api.post(`${API}/google/events`, payload);
      toast.success(
        data?.event?.meetLink ? "Event created with a Meet link" : "Event added to Google Calendar"
      );
      onOpenChange(false);
      onCreated?.(data?.event);
    } catch (ex) {
      const detail =
        ex?.response?.data?.detail ||
        ex?.response?.data?.message ||
        "Could not create the event.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Google Calendar Event</DialogTitle>
          <DialogDescription>
            Creates the event on your connected Google Calendar. Attendees are invited by
            Google directly.
          </DialogDescription>
        </DialogHeader>

        <form id="google-event-form" onSubmit={handleSubmit} className="grid gap-4 py-2">
          <div>
            <Label htmlFor="ge-summary">Title</Label>
            <Input
              id="ge-summary"
              autoFocus
              value={form.summary}
              onChange={(e) => set({ summary: e.target.value })}
              placeholder="e.g. Site review with client"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="ge-allday"
              checked={form.allDay}
              onCheckedChange={(v) => set({ allDay: !!v })}
            />
            <Label htmlFor="ge-allday" className="cursor-pointer font-normal">
              All-day event
            </Label>
          </div>

          {form.allDay ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="ge-start-date">Start date</Label>
                <Input
                  id="ge-start-date"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    set({
                      startDate,
                      // Keep the range valid rather than letting it invert.
                      endDate:
                        form.endDate && form.endDate >= startDate ? form.endDate : startDate,
                    });
                  }}
                />
              </div>
              <div>
                <Label htmlFor="ge-end-date">End date</Label>
                <Input
                  id="ge-end-date"
                  type="date"
                  min={form.startDate}
                  value={form.endDate}
                  onChange={(e) => set({ endDate: e.target.value })}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Inclusive — the event covers this day.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="ge-start-dt">Starts</Label>
                <Input
                  id="ge-start-dt"
                  type="datetime-local"
                  value={form.startDateTime}
                  onChange={(e) => {
                    const startDateTime = e.target.value;
                    const patch = { startDateTime };
                    // Drag the end along so it never sits before the start.
                    if (form.endDateTime <= startDateTime) {
                      const [d, t] = startDateTime.split("T");
                      const [h, m] = t.split(":").map(Number);
                      const bumped = new Date(`${d}T${t}:00`);
                      bumped.setHours(h + 1, m);
                      patch.endDateTime = toDateTimeInput(bumped);
                    }
                    set(patch);
                  }}
                />
              </div>
              <div>
                <Label htmlFor="ge-end-dt">Ends</Label>
                <Input
                  id="ge-end-dt"
                  type="datetime-local"
                  min={form.startDateTime}
                  value={form.endDateTime}
                  onChange={(e) => set({ endDateTime: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              id="ge-meet"
              checked={form.withMeet}
              onCheckedChange={(v) => set({ withMeet: !!v })}
            />
            <Label htmlFor="ge-meet" className="flex cursor-pointer items-center gap-1.5 font-normal">
              <Video className="h-4 w-4 text-muted-foreground" />
              Add a Google Meet link
            </Label>
          </div>

          <div>
            <Label htmlFor="ge-attendees">Attendees</Label>
            <Input
              id="ge-attendees"
              value={form.attendees}
              onChange={(e) => set({ attendees: e.target.value })}
              placeholder="name@example.com, someone@example.com"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Comma separated. Google emails the invitations.
            </p>
          </div>

          <div>
            <Label htmlFor="ge-location">Location</Label>
            <Input
              id="ge-location"
              value={form.location}
              onChange={(e) => set({ location: e.target.value })}
              placeholder="Office, site address, or leave blank"
            />
          </div>

          <div>
            <Label htmlFor="ge-description">Description</Label>
            <Textarea
              id="ge-description"
              rows={3}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Agenda, notes, links…"
            />
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="google-event-form" disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
